/**
 * CalendarQueueService - Centralized queue management with optimistic updates
 * Features:
 * - Product data cache for fast lookups
 * - Event-driven architecture
 * - Optimistic UI updates
 * - Visual feedback (toasts)
 * - Error handling with rollback
 */
class CalendarQueueService {
  constructor() {
    this.productCache = new Map(); // Cache product data by productId
    this.pendingOperations = new Map(); // Track pending operations for rollback
    this.init();
  }

  init() {
    // Listen for calendar events
    document.addEventListener('calendar:item:added', this.handleItemAdded.bind(this));
    document.addEventListener('calendar:item:removed', this.handleItemRemoved.bind(this));
    document.addEventListener('calendar:item:reordered', this.handleItemReordered.bind(this));
  }

  /**
   * Cache product data for faster lookups
   */
  cacheProductData(productId, data) {
    this.productCache.set(productId, {
      ...data,
      cachedAt: Date.now()
    });
  }

  /**
   * Get cached product data or extract from DOM
   */
  getProductData(productId, form = null) {
    // Check cache first
    const cached = this.productCache.get(productId);
    if (cached && (Date.now() - cached.cachedAt < 300000)) { // 5 min cache
      return { productTitle: cached.title, productImage: cached.image };
    }

    // Extract from DOM
    let productTitle = 'Product';
    let productImage = '';

    if (form) {
      // Method 1: Data attributes (fastest)
      if (form.dataset.productTitle) {
        productTitle = form.dataset.productTitle;
      }
      if (form.dataset.productImage) {
        productImage = form.dataset.productImage;
      }

      // Method 2: From product-block or product-form
      if (productTitle === 'Product' || !productImage) {
        const productBlock = form.closest('product-block');
        const productForm = form.closest('product-form');

        if (productBlock) {
          if (productTitle === 'Product') {
            const cardTitle = productBlock.querySelector('.product-block__title, h3, .product-title');
            if (cardTitle) productTitle = cardTitle.textContent.trim();
          }
          if (!productImage) {
            const cardImage = productBlock.querySelector('img[src*="products"], .product-block__image img');
            if (cardImage) productImage = cardImage.src || cardImage.dataset.src || '';
          }
        } else if (productForm) {
          if (productTitle === 'Product') {
            const pageTitle = productForm.querySelector('h1') || document.querySelector('h1.product-title');
            if (pageTitle) productTitle = pageTitle.textContent.trim();
          }
          if (!productImage) {
            const pageImage = productForm.querySelector('img[src*="products"]') || 
                             document.querySelector('.product-media img, .media-gallery img');
            if (pageImage) productImage = pageImage.src || pageImage.dataset.src || '';
          }
        }
      }
    }

    // Method 3: ProductJson script tag
    if (productTitle === 'Product' || !productImage) {
      const productJsonScript = document.querySelector('script[id^="ProductJson-"]');
      if (productJsonScript) {
        try {
          const productData = JSON.parse(productJsonScript.textContent);
          if (productTitle === 'Product') productTitle = productData.title || productTitle;
          if (!productImage) {
            if (productData.featured_media) {
              productImage = productData.featured_media.preview?.image?.src || 
                            productData.featured_media.src || '';
            } else if (productData.images && productData.images.length > 0) {
              productImage = productData.images[0];
            }
          }
        } catch (e) {
          console.warn("Could not parse ProductJson:", e);
        }
      }
    }

    // Method 4: Fallback by product ID
    if (productTitle === 'Product') {
      const productElement = document.querySelector(`[data-product-id="${productId}"]`);
      if (productElement) {
        const titleEl = productElement.querySelector('.product-title, h3, h2, .product-block__title');
        if (titleEl) productTitle = titleEl.textContent.trim();
      }
    }

    // Cache the result
    this.cacheProductData(productId, { title: productTitle, image: productImage });

    return { productTitle, productImage };
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'success', duration = 3000) {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `calendar-toast calendar-toast--${type} calendar-toast--hidden`;
    toast.innerHTML = `
      <div class="calendar-toast__message">${message}</div>
      <button class="calendar-toast__close" aria-label="Close">×</button>
    `;

    // Add styles if not already added
    if (!document.getElementById('calendar-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'calendar-toast-styles';
      style.textContent = `
        .calendar-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          min-width: 280px;
          max-width: 400px;
          padding: 16px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          transition: all 0.3s ease;
          font-size: 14px;
          line-height: 1.4;
        }
        .calendar-toast--success {
          background: #10b981;
          color: white;
        }
        .calendar-toast--error {
          background: #ef4444;
          color: white;
        }
        .calendar-toast--info {
          background: #3b82f6;
          color: white;
        }
        .calendar-toast--hidden {
          opacity: 0;
          transform: translateX(100%);
        }
        .calendar-toast__message {
          flex: 1;
        }
        .calendar-toast__close {
          background: none;
          border: none;
          color: inherit;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.8;
        }
        .calendar-toast__close:hover {
          opacity: 1;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    // Show toast
    setTimeout(() => toast.classList.remove('calendar-toast--hidden'), 10);

    // Close button
    toast.querySelector('.calendar-toast__close').addEventListener('click', () => {
      this.dismissToast(toast);
    });

    // Auto-dismiss
    setTimeout(() => this.dismissToast(toast), duration);
  }

  dismissToast(toast) {
    toast.classList.add('calendar-toast--hidden');
    setTimeout(() => toast.remove(), 300);
  }

  /**
   * Add item to calendar (optimistic update + server sync)
   */
  async addItem(productId, date, form = null) {
    const operationId = `add-${productId}-${Date.now()}`;
    
    try {
      // Get product data
      const { productTitle, productImage } = this.getProductData(productId, form);
      
      // Create optimistic event data
      const optimisticEvent = {
        product_id: productId,
        date: date,
        name: productTitle,
        title: productTitle,
        image_url: productImage,
        premium_value: 0
      };

      // Dispatch event for optimistic update
      document.dispatchEvent(new CustomEvent('calendar:item:added', {
        detail: { event: optimisticEvent, operationId }
      }));

      // Server sync
      const addData = {
        customerId: window.customerId,
        productId: productId,
        date: date,
        shop: window.shopUrl,
      };

      const resp = await fetchAppProxyData("add_customer_product_to_calendar", addData);

      if (resp && (resp.success === true || resp.success === undefined)) {
        // Success - refresh calendar
        this.clearCalendarCache();

        document.dispatchEvent(new CustomEvent('calendar:refresh', { detail: { operationId } }));
        this.pendingOperations.delete(operationId);
        return { success: true, data: resp };
      } else {
        throw new Error(resp?.message || 'Failed to add to calendar');
      }
    } catch (error) {
      console.error('Error adding item to calendar:', error);
      this.showToast(`Failed to add to queue: ${error.message}`, 'error');
      
      // Rollback optimistic update
      document.dispatchEvent(new CustomEvent('calendar:rollback', { 
        detail: { operationId, type: 'add' } 
      }));
      
      // Refresh to restore correct state
      this.clearCalendarCache();
      document.dispatchEvent(new CustomEvent('calendar:refresh'));
      this.pendingOperations.delete(operationId);
      
      return { success: false, error };
    }
  }

  /**
   * Remove item from calendar (optimistic update + server sync)
   */
  async removeItem(productId, date) {
    const operationId = `remove-${productId}-${Date.now()}`;
    
    try {
      // Dispatch event for optimistic removal
      document.dispatchEvent(new CustomEvent('calendar:item:removed', {
        detail: { productId, date, operationId }
      }));

      // Server sync
      const result = await fetchAppProxyData("remove_customer_product_from_calendar", {
        customerId: window.customerId,
        productId: productId,
        date: date,
        shop: window.shopUrl,
      });

      if (result && (result.status === "success" || result.success === true)) {
        this.clearCalendarCache();

        document.dispatchEvent(new CustomEvent('calendar:refresh', { detail: { operationId } }));
        this.pendingOperations.delete(operationId);
        return { success: true, data: result };
      } else {
        throw new Error(result?.message || 'Failed to remove from calendar');
      }
    } catch (error) {
      console.error('Error removing item from calendar:', error);
      this.showToast(`Failed to remove: ${error.message}`, 'error');
      
      // Rollback
      document.dispatchEvent(new CustomEvent('calendar:rollback', { 
        detail: { operationId, type: 'remove' } 
      }));
      
      // Refresh to restore correct state
      this.clearCalendarCache();
      document.dispatchEvent(new CustomEvent('calendar:refresh'));
      this.pendingOperations.delete(operationId);
      
      return { success: false, error };
    }
  }

  /**
   * Reorder items (optimistic update + server sync)
   */
  async reorderItem(dragProductId, dragDate, dropProductId, dropDate) {
    const operationId = `reorder-${Date.now()}`;
    
    try {
      // Dispatch event for optimistic reorder
      document.dispatchEvent(new CustomEvent('calendar:item:reordered', {
        detail: { dragProductId, dragDate, dropProductId, dropDate, operationId }
      }));


      // Server sync
      const result = await fetchAppProxyData("change_customer_product_date", {
        customerId: window.customerId,
        dropProductId: dropProductId,
        dropDate: dropDate,
        dragProductId: dragProductId,
        dragDate: dragDate,
      });

      if (result && (result.status === "success" || result.success === true)) {
        this.clearCalendarCache();

        document.dispatchEvent(new CustomEvent('calendar:refresh', { detail: { operationId } }));
        this.pendingOperations.delete(operationId);
        return { success: true, data: result };
      } else {
        throw new Error(result?.message || 'Failed to reorder');
      }
    } catch (error) {
      console.error('Error reordering items:', error);
      this.showToast(`Failed to reorder: ${error.message}`, 'error');
      
      // Rollback
      document.dispatchEvent(new CustomEvent('calendar:rollback', { 
        detail: { operationId, type: 'reorder' } 
      }));
      
      // Refresh to restore correct state
      this.clearCalendarCache();
      document.dispatchEvent(new CustomEvent('calendar:refresh'));
      this.pendingOperations.delete(operationId);
      
      return { success: false, error };
    }
  }

  /**
   * Clear calendar cache
   */
  clearCalendarCache() {
    const calendarCacheKey = 'subscription_data_with_events_no_fragrance';
    delete window[calendarCacheKey];
    if (window.subscriptionDataFetchPromise) {
      window.subscriptionDataFetchPromise = null;
    }
    // Clear Liquid-injected calendar data so drawer fetches fresh from proxy
    window.__calendarQueueData = null;
  }

  /**
   * Event handlers
   */
  handleItemAdded(event) {
    // Calendar drawer will listen and handle this
    console.log('📅 Calendar item added event:', event.detail);
  }

  handleItemRemoved(event) {
    // Calendar drawer will listen and handle this
    console.log('🗑️ Calendar item removed event:', event.detail);
  }

  handleItemReordered(event) {
    // Calendar drawer will listen and handle this
    console.log('🔄 Calendar item reordered event:', event.detail);
  }
}

// Initialize singleton instance
window.calendarQueueService = new CalendarQueueService();




